'use client';

import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import Image from 'next/image';
import { motion } from 'motion/react';

type TeamMember = {
  name: string;
  role: string;
  bio: string;
  gradient: string;
  image: string;
  accent: string;
};

const director: TeamMember = {
  name: 'Seema Singh',
  role: 'Director',
  bio: 'Guiding corporate strategy, governance, and long-term growth to ensure operational excellence across all initiatives.',
  gradient: 'from-rose-400 via-fuchsia-500 to-indigo-500',
  image: 'Seema Singh.png',
  accent: 'text-rose-500',
};

const founders: TeamMember[] = [
  {
    name: 'Sumit Singh',
    role: 'Founder & CEO',
    bio: 'Leading product vision and enterprise architecture to ensure every system we build scales flawlessly from day one.',
    gradient: 'from-sky-400 via-indigo-400 to-purple-500',
    image: 'sumit singh photo.png',
    accent: 'text-sky-500',
  },
  {
    name: 'Samarth Yadav',
    role: 'Co-Founder & COO',
    bio: 'Bridging the gap between complex engineering and business goals, ensuring smooth delivery and long-term market success.',
    gradient: 'from-emerald-400 via-teal-400 to-cyan-500',
    image: 'Samarth.png',
    accent: 'text-emerald-500',
  },
];

const teamMembers: TeamMember[] = [director, ...founders];

const AUTO_SCROLL_DELAY = 4000;
const CARD_GAP = 16;

export default function OurTeam() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [activeIndex, setActiveIndex] = useState(0);

  const clearAutoScroll = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const getCardWidth = useCallback(() => {
    const container = scrollRef.current;

    if (!container || container.children.length === 0) {
      return 0;
    }

    const firstCard = container.children[0] as HTMLElement;

    return firstCard.offsetWidth + CARD_GAP;
  }, []);

  const scrollToCard = useCallback(
    (index: number) => {
      const container = scrollRef.current;
      const cardWidth = getCardWidth();

      if (!container || cardWidth === 0) {
        return;
      }

      container.scrollTo({
        left: index * cardWidth,
        behavior: 'smooth',
      });

      setActiveIndex(index);
    },
    [getCardWidth],
  );

  const startAutoScroll = useCallback(() => {
    clearAutoScroll();

    intervalRef.current = setInterval(() => {
      setActiveIndex((currentIndex) => {
        const nextIndex =
          currentIndex >= teamMembers.length - 1
            ? 0
            : currentIndex + 1;

        const container = scrollRef.current;
        const cardWidth = getCardWidth();

        if (container && cardWidth > 0) {
          container.scrollTo({
            left: nextIndex * cardWidth,
            behavior: 'smooth',
          });
        }

        return nextIndex;
      });
    }, AUTO_SCROLL_DELAY);
  }, [clearAutoScroll, getCardWidth]);

  const handleScroll = useCallback(() => {
    const container = scrollRef.current;
    const cardWidth = getCardWidth();

    if (!container || cardWidth === 0) {
      return;
    }

    const calculatedIndex = Math.round(
      container.scrollLeft / cardWidth,
    );

    const safeIndex = Math.min(
      Math.max(calculatedIndex, 0),
      teamMembers.length - 1,
    );

    setActiveIndex(safeIndex);
  }, [getCardWidth]);

  const handleDotClick = (index: number) => {
    scrollToCard(index);
    startAutoScroll();
  };

  useEffect(() => {
    startAutoScroll();

    return () => {
      clearAutoScroll();
    };
  }, [clearAutoScroll, startAutoScroll]);

  return (
    <section data-component-id="src/app/about/components/OurTeam.tsx:157:4" data-component-path="src/app/about/components/OurTeam.tsx" data-component-line="157" data-component-end-line="360" data-component-file="OurTeam.tsx" data-component-name="section" data-component-content="%7B%22elementName%22%3A%22section%22%2C%22className%22%3A%22bg-transparent%20px-4%20py-20%20sm%3Apx-6%22%7D" className="bg-transparent px-4 py-20 sm:px-6">
      <div data-component-id="src/app/about/components/OurTeam.tsx:158:6" data-component-path="src/app/about/components/OurTeam.tsx" data-component-line="158" data-component-end-line="359" data-component-file="OurTeam.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mx-auto%20max-w-7xl%22%7D" className="mx-auto max-w-7xl">
        {/* Header */}
        <motion.div data-component-id="src/app/about/components/OurTeam.tsx:160:8" data-component-path="src/app/about/components/OurTeam.tsx" data-component-line="160" data-component-end-line="191" data-component-file="OurTeam.tsx" data-component-name="motion.div" data-component-content="%7B%22elementName%22%3A%22motion.div%22%2C%22className%22%3A%22mx-auto%20mb-16%20max-w-3xl%20text-center%22%7D"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.5 }}
          className="mx-auto mb-16 max-w-3xl text-center"
        >
          <span data-component-id="src/app/about/components/OurTeam.tsx:167:10" data-component-path="src/app/about/components/OurTeam.tsx" data-component-line="167" data-component-end-line="169" data-component-file="OurTeam.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22mb-4%20inline-flex%20items-center%20gap-1.5%20rounded-full%20border%20border-blue-100%20bg-blue-50%20px-3%20py-1%20text-%5B10px%5D%20font-bold%20uppercase%20tracking-widest%20text-blue-600%22%2C%22textContent%22%3A%22The%20Leadership%20Team%22%7D" className="mb-4 inline-flex items-center gap-1.5 rounded-full border border-blue-100 bg-blue-50 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-blue-600">
            The Leadership Team
          </span>

          <h2 data-component-id="src/app/about/components/OurTeam.tsx:171:10" data-component-path="src/app/about/components/OurTeam.tsx" data-component-line="171" data-component-end-line="185" data-component-file="OurTeam.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22mb-6%20text-3xl%20font-extrabold%20tracking-tight%20text-slate-900%20md%3Atext-4xl%22%2C%22textContent%22%3A%22Meet%20Our%22%7D" className="mb-6 text-3xl font-extrabold tracking-tight text-slate-900 md:text-4xl">
            Meet Our{' '}
            <span data-component-id="src/app/about/components/OurTeam.tsx:173:12" data-component-path="src/app/about/components/OurTeam.tsx" data-component-line="173" data-component-end-line="184" data-component-file="OurTeam.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22pr-2%20font-display%20italic%22%2C%22textContent%22%3A%22Leadership%22%7D"
              className="pr-2 font-display italic"
              style={{
                background:
                  'linear-gradient(135deg, #0ea5e9 0%, #6366f1 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              Leadership
            </span>
          </h2>

          <p data-component-id="src/app/about/components/OurTeam.tsx:187:10" data-component-path="src/app/about/components/OurTeam.tsx" data-component-line="187" data-component-end-line="190" data-component-file="OurTeam.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20leading-relaxed%20text-slate-500%20sm%3Atext-base%22%2C%22textContent%22%3A%22Meet%20the%20people%20driving%20our%20vision%2C%20technology%2C%20operations%2C%5Cn%20%20%20%20%20%20%20%20%20%20%20%20and%20long-term%20business%20growth.%22%7D" className="text-sm leading-relaxed text-slate-500 sm:text-base">
            Meet the people driving our vision, technology, operations,
            and long-term business growth.
          </p>
        </motion.div>

        {/* Mobile Carousel */}
        <div data-component-id="src/app/about/components/OurTeam.tsx:194:8" data-component-path="src/app/about/components/OurTeam.tsx" data-component-line="194" data-component-end-line="269" data-component-file="OurTeam.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mb-12%20md%3Ahidden%22%7D" className="mb-12 md:hidden">
          <div data-component-id="src/app/about/components/OurTeam.tsx:195:10" data-component-path="src/app/about/components/OurTeam.tsx" data-component-line="195" data-component-end-line="248" data-component-file="OurTeam.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20snap-x%20snap-mandatory%20gap-4%20overflow-x-auto%20pb-4%22%7D"
            ref={scrollRef}
            onScroll={handleScroll}
            onTouchStart={clearAutoScroll}
            onTouchEnd={startAutoScroll}
            onMouseEnter={clearAutoScroll}
            onMouseLeave={startAutoScroll}
            className="flex snap-x snap-mandatory gap-4 overflow-x-auto pb-4"
            style={{
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
            }}
          >
            {teamMembers.map((member, index) => (
              <motion.article data-component-id="src/app/about/components/OurTeam.tsx:209:14" data-component-path="src/app/about/components/OurTeam.tsx" data-component-line="209" data-component-end-line="246" data-component-file="OurTeam.tsx" data-component-name="motion.article" data-component-content="%7B%22elementName%22%3A%22motion.article%22%2C%22className%22%3A%22min-w-%5B85%25%5D%20snap-center%20rounded-2xl%20border%20border-slate-100%20bg-white%20p-8%20text-center%20shadow-%5B0_8px_30px_rgba(0%2C0%2C0%2C0.04)%5D%22%7D"
                key={member.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{
                  duration: 0.45,
                  delay: index * 0.08,
                }}
                className="min-w-[85%] snap-center rounded-2xl border border-slate-100 bg-white p-8 text-center shadow-[0_8px_30px_rgba(0,0,0,0.04)]"
              >
                <div data-component-id="src/app/about/components/OurTeam.tsx:220:16" data-component-path="src/app/about/components/OurTeam.tsx" data-component-line="220" data-component-end-line="231" data-component-file="OurTeam.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
                  className={`mx-auto mb-6 h-28 w-28 overflow-hidden rounded-full bg-gradient-to-tr p-1 ${member.gradient}`}
                >
                  <Image data-component-id="src/app/about/components/OurTeam.tsx:223:18" data-component-path="src/app/about/components/OurTeam.tsx" data-component-line="223" data-component-end-line="230" data-component-file="OurTeam.tsx" data-component-name="Image" data-component-content="%7B%22elementName%22%3A%22Image%22%2C%22className%22%3A%22h-full%20w-full%20rounded-full%20border-%5B3px%5D%20border-white%20object-cover%22%7D"
                    src={`/assets/images/${member.image}`}
                    alt={`${member.name} - ${member.role}`}
                    width={112}
                    height={112}
                    sizes="112px"
                    className="h-full w-full rounded-full border-[3px] border-white object-cover"
                  />
                </div>

                <h3 data-component-id="src/app/about/components/OurTeam.tsx:233:16" data-component-path="src/app/about/components/OurTeam.tsx" data-component-line="233" data-component-end-line="235" data-component-file="OurTeam.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22mb-1%20text-xl%20font-bold%20text-slate-900%22%7D" className="mb-1 text-xl font-bold text-slate-900">
                  {member.name}
                </h3>

                <p data-component-id="src/app/about/components/OurTeam.tsx:237:16" data-component-path="src/app/about/components/OurTeam.tsx" data-component-line="237" data-component-end-line="241" data-component-file="OurTeam.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%7D"
                  className={`mb-4 text-xs font-bold uppercase tracking-widest ${member.accent}`}
                >
                  {member.role}
                </p>

                <p data-component-id="src/app/about/components/OurTeam.tsx:243:16" data-component-path="src/app/about/components/OurTeam.tsx" data-component-line="243" data-component-end-line="245" data-component-file="OurTeam.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22w-full%20border-t%20border-slate-100%20pt-5%20text-%5B13px%5D%20leading-relaxed%20text-slate-500%22%7D" className="w-full border-t border-slate-100 pt-5 text-[13px] leading-relaxed text-slate-500">
                  {member.bio}
                </p>
              </motion.article>
            ))}
          </div>

          {/* Mobile Dots */}
          <div data-component-id="src/app/about/components/OurTeam.tsx:251:10" data-component-path="src/app/about/components/OurTeam.tsx" data-component-line="251" data-component-end-line="268" data-component-file="OurTeam.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mt-5%20flex%20items-center%20justify-center%20gap-2%22%7D" className="mt-5 flex items-center justify-center gap-2">
            {teamMembers.map((member, index) => (
              <button data-component-id="src/app/about/components/OurTeam.tsx:253:14" data-component-path="src/app/about/components/OurTeam.tsx" data-component-line="253" data-component-end-line="266" data-component-file="OurTeam.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22type%22%3A%22button%22%7D"
                key={member.name}
                type="button"
                aria-label={`Show ${member.name}`}
                aria-current={
                  activeIndex === index ? 'true' : undefined
                }
                onClick={() => handleDotClick(index)}
                className={`rounded-full transition-all duration-300 ${
                  activeIndex === index
                    ? 'h-2 w-6 bg-sky-500'
                    : 'h-2 w-2 bg-slate-300 hover:bg-slate-400'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Desktop Director */}
        <div data-component-id="src/app/about/components/OurTeam.tsx:272:8" data-component-path="src/app/about/components/OurTeam.tsx" data-component-line="272" data-component-end-line="313" data-component-file="OurTeam.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mb-12%20hidden%20justify-center%20md%3Aflex%22%7D" className="mb-12 hidden justify-center md:flex">
          <motion.article data-component-id="src/app/about/components/OurTeam.tsx:273:10" data-component-path="src/app/about/components/OurTeam.tsx" data-component-line="273" data-component-end-line="312" data-component-file="OurTeam.tsx" data-component-name="motion.article" data-component-content="%7B%22elementName%22%3A%22motion.article%22%2C%22className%22%3A%22relative%20flex%20w-full%20max-w-sm%20flex-col%20items-center%20rounded-2xl%20border%20border-slate-100%20bg-white%20p-8%20text-center%20shadow-%5B0_12px_40px_rgba(0%2C0%2C0%2C0.06)%5D%22%7D"
            initial={{ opacity: 0, y: 25 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            whileHover={{ y: -6 }}
            transition={{ duration: 0.45 }}
            className="relative flex w-full max-w-sm flex-col items-center rounded-2xl border border-slate-100 bg-white p-8 text-center shadow-[0_12px_40px_rgba(0,0,0,0.06)]"
          >
            <span data-component-id="src/app/about/components/OurTeam.tsx:281:12" data-component-path="src/app/about/components/OurTeam.tsx" data-component-line="281" data-component-end-line="283" data-component-file="OurTeam.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22absolute%20right-5%20top-5%20rounded-full%20bg-rose-50%20px-3%20py-1%20text-%5B10px%5D%20font-bold%20uppercase%20tracking-wider%20text-rose-500%22%2C%22textContent%22%3A%22Leadership%22%7D" className="absolute right-5 top-5 rounded-full bg-rose-50 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-rose-500">
              Leadership
            </span>

            <div data-component-id="src/app/about/components/OurTeam.tsx:285:12" data-component-path="src/app/about/components/OurTeam.tsx" data-component-line="285" data-component-end-line="297" data-component-file="OurTeam.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
              className={`mb-6 h-32 w-32 overflow-hidden rounded-full bg-gradient-to-tr p-1 ${director.gradient}`}
            >
              <Image data-component-id="src/app/about/components/OurTeam.tsx:288:14" data-component-path="src/app/about/components/OurTeam.tsx" data-component-line="288" data-component-end-line="296" data-component-file="OurTeam.tsx" data-component-name="Image" data-component-content="%7B%22elementName%22%3A%22Image%22%2C%22className%22%3A%22h-full%20w-full%20rounded-full%20border-%5B3px%5D%20border-white%20object-cover%22%7D"
                src={`/assets/images/${director.image}`}
                alt={`${director.name} - ${director.role}`}
                width={128}
                height={128}
                sizes="128px"
                priority
                className="h-full w-full rounded-full border-[3px] border-white object-cover"
              />
            </div>

            <h3 data-component-id="src/app/about/components/OurTeam.tsx:299:12" data-component-path="src/app/about/components/OurTeam.tsx" data-component-line="299" data-component-end-line="301" data-component-file="OurTeam.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22mb-1%20text-2xl%20font-bold%20text-slate-900%22%7D" className="mb-1 text-2xl font-bold text-slate-900">
              {director.name}
            </h3>

            <p data-component-id="src/app/about/components/OurTeam.tsx:303:12" data-component-path="src/app/about/components/OurTeam.tsx" data-component-line="303" data-component-end-line="307" data-component-file="OurTeam.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%7D"
              className={`mb-4 text-xs font-bold uppercase tracking-widest ${director.accent}`}
            >
              {director.role}
            </p>

            <p data-component-id="src/app/about/components/OurTeam.tsx:309:12" data-component-path="src/app/about/components/OurTeam.tsx" data-component-line="309" data-component-end-line="311" data-component-file="OurTeam.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22w-full%20border-t%20border-slate-100%20pt-5%20text-sm%20leading-relaxed%20text-slate-500%22%7D" className="w-full border-t border-slate-100 pt-5 text-sm leading-relaxed text-slate-500">
              {director.bio}
            </p>
          </motion.article>
        </div>

        {/* Desktop Founders Grid */}
        <div data-component-id="src/app/about/components/OurTeam.tsx:316:8" data-component-path="src/app/about/components/OurTeam.tsx" data-component-line="316" data-component-end-line="358" data-component-file="OurTeam.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22hidden%20gap-8%20md%3Agrid%20md%3Agrid-cols-2%20md%3Apx-16%20lg%3Apx-32%22%7D" className="hidden gap-8 md:grid md:grid-cols-2 md:px-16 lg:px-32">
          {founders.map((member, index) => (
            <motion.article data-component-id="src/app/about/components/OurTeam.tsx:318:12" data-component-path="src/app/about/components/OurTeam.tsx" data-component-line="318" data-component-end-line="356" data-component-file="OurTeam.tsx" data-component-name="motion.article" data-component-content="%7B%22elementName%22%3A%22motion.article%22%2C%22className%22%3A%22flex%20flex-col%20items-center%20rounded-2xl%20border%20border-slate-100%20bg-white%20p-8%20text-center%20shadow-%5B0_8px_30px_rgba(0%2C0%2C0%2C0.04)%5D%20transition-shadow%20duration-300%20hover%3Ashadow-%5B0_15px_40px_rgba(0%2C0%2C0%2C0.08)%5D%22%7D"
              key={member.name}
              initial={{ opacity: 0, y: 25 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.25 }}
              whileHover={{ y: -6 }}
              transition={{
                duration: 0.45,
                delay: index * 0.1,
              }}
              className="flex flex-col items-center rounded-2xl border border-slate-100 bg-white p-8 text-center shadow-[0_8px_30px_rgba(0,0,0,0.04)] transition-shadow duration-300 hover:shadow-[0_15px_40px_rgba(0,0,0,0.08)]"
            >
              <div data-component-id="src/app/about/components/OurTeam.tsx:330:14" data-component-path="src/app/about/components/OurTeam.tsx" data-component-line="330" data-component-end-line="341" data-component-file="OurTeam.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
                className={`mb-6 h-28 w-28 overflow-hidden rounded-full bg-gradient-to-tr p-1 ${member.gradient}`}
              >
                <Image data-component-id="src/app/about/components/OurTeam.tsx:333:16" data-component-path="src/app/about/components/OurTeam.tsx" data-component-line="333" data-component-end-line="340" data-component-file="OurTeam.tsx" data-component-name="Image" data-component-content="%7B%22elementName%22%3A%22Image%22%2C%22className%22%3A%22h-full%20w-full%20rounded-full%20border-%5B3px%5D%20border-white%20object-cover%22%7D"
                  src={`/assets/images/${member.image}`}
                  alt={`${member.name} - ${member.role}`}
                  width={112}
                  height={112}
                  sizes="112px"
                  className="h-full w-full rounded-full border-[3px] border-white object-cover"
                />
              </div>

              <h3 data-component-id="src/app/about/components/OurTeam.tsx:343:14" data-component-path="src/app/about/components/OurTeam.tsx" data-component-line="343" data-component-end-line="345" data-component-file="OurTeam.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22mb-1%20text-xl%20font-bold%20text-slate-900%22%7D" className="mb-1 text-xl font-bold text-slate-900">
                {member.name}
              </h3>

              <p data-component-id="src/app/about/components/OurTeam.tsx:347:14" data-component-path="src/app/about/components/OurTeam.tsx" data-component-line="347" data-component-end-line="351" data-component-file="OurTeam.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%7D"
                className={`mb-4 text-xs font-bold uppercase tracking-widest ${member.accent}`}
              >
                {member.role}
              </p>

              <p data-component-id="src/app/about/components/OurTeam.tsx:353:14" data-component-path="src/app/about/components/OurTeam.tsx" data-component-line="353" data-component-end-line="355" data-component-file="OurTeam.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22w-full%20flex-1%20border-t%20border-slate-100%20pt-5%20text-%5B13px%5D%20leading-relaxed%20text-slate-500%22%7D" className="w-full flex-1 border-t border-slate-100 pt-5 text-[13px] leading-relaxed text-slate-500">
                {member.bio}
              </p>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}